import { forwardRef, SyntheticEvent, useImperativeHandle, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import { MoveRight, Pencil, CircleCheck, Check, CircleOff } from 'lucide-react';
import { useUnitFocus } from '../../custom-hooks/useUnitFocus';
import { useUnitFocusEligibility } from '../../custom-hooks/useFocusEligibility';
import { useRoadmapMembership } from '../../custom-hooks/useFocusClassData';
import { useAppSelector } from '../../redux/hooks';
import { selectFocuses, selectActiveFocusClassIds } from '../../redux/reducers/focusSlice';
import {
  classIdsWithFocusRecipients,
  resolveFocusRestrictDefault,
  resolveNextUnitId,
} from '../../businessLogicService/focusService';
import { buildFocusMovedMessage, buildFocusEndedMessage } from './focusMessages';
import { UnitSelectStep } from './UnitSelectStep';
import { EndFocusStep, EndScope } from './EndFocusStep';
import { FocusReachSummary } from './FocusReachSummary';
import { FocusSuccessToast, FocusSuccessToastHandle } from './FocusSuccessToast';
import { useUnitFocusEvent } from '../../custom-hooks/useUnitFocusEvent';

export interface MoveUnitOption {
  unitName: string;
  summary: string;
  value: string;
}

interface Props {
  fromTargetId: string;
  roadmapId: string;
  roadmapName: string;
  classId?: string;
  className?: string;
  units: MoveUnitOption[];
  completedUnitIds: ReadonlySet<string>;
  onMoved?: (toTargetId: string) => void;
  onEnded?: () => void;
  /** Fired when the teacher starts the End-focus flow — stops the onboarding pulse. */
  onEndFocusFlow?: () => void;
}

export interface MoveFocusPopoverHandle {
  toggle: (e: SyntheticEvent) => void;
}

const CONFIRM_BTN =
  '!tw-w-full !tw-justify-center !tw-rounded-full !tw-border-indigo-600 !tw-bg-indigo-600 !tw-px-6 !tw-py-3 !tw-font-bold';
const EYEBROW = 'tw-font-atkinson tw-text-xs tw-font-bold tw-uppercase';
const UNIT_CARD = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1 tw-rounded-xl tw-border-solid tw-border-2 tw-p-3';

export const MoveFocusPopover = forwardRef<MoveFocusPopoverHandle, Props>(function MoveFocusPopover(
  {
    fromTargetId,
    roadmapId,
    roadmapName,
    classId = '',
    className = '',
    units,
    completedUnitIds,
    onMoved,
    onEnded,
    onEndFocusFlow,
  },
  ref,
) {
  const op = useRef<OverlayPanel>(null);
  const focusToast = useRef<FocusSuccessToastHandle>(null);
  useImperativeHandle(ref, () => ({ toggle: (e: SyntheticEvent) => op.current?.toggle(e) }), []);

  const captureUnitFocusEvent = useUnitFocusEvent();
  const { moveFocus, endFocus } = useUnitFocus();
  const focuses = useAppSelector(selectFocuses);
  const sharedClassIds = useAppSelector(selectActiveFocusClassIds)[fromTargetId];

  const orderedUnitIds = units.map(u => u.value);
  const defaultTargetId = () => resolveNextUnitId(orderedUnitIds, fromTargetId, completedUnitIds);

  const [open, setOpen] = useState(false);
  const [toTargetId, setToTargetId] = useState('');
  const [defaultToTargetId, setDefaultToTargetId] = useState('');
  const [step, setStep] = useState<'confirm' | 'select' | 'end'>('confirm');
  const [applyToAllClasses, setApplyToAllClasses] = useState(true);
  const [restrict, setRestrict] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmError, setConfirmError] = useState(false);
  const [endScope, setEndScope] = useState<EndScope>('this');
  const [ending, setEnding] = useState(false);
  const [endError, setEndError] = useState(false);

  const sharedCount = sharedClassIds?.length ?? (classId ? 1 : 0);
  const allSharingClassIds = sharedClassIds?.length ? sharedClassIds : classId ? [classId] : [];
  const selectedClassIds = applyToAllClasses ? allSharingClassIds : classId ? [classId] : [];

  const membership = useRoadmapMembership(open, selectedClassIds, roadmapId);
  const memberFilter: ReadonlySet<string> | null = membership.loading || membership.error ? null : membership.memberIds;
  const elig = useUnitFocusEligibility(open, toTargetId, selectedClassIds, memberFilter);
  const loadingReach = membership.loading || elig.loading;
  const reached = elig.included.length;
  const total = elig.included.length + elig.excluded.length;

  const fromUnit = units.find(u => u.value === fromTargetId);
  const toUnit = units.find(u => u.value === toTargetId);

  const endActive = open && step === 'end';
  const thisClassMembers = useRoadmapMembership(endActive, classId ? [classId] : [], roadmapId);
  const allClassMembers = useRoadmapMembership(endActive && sharedCount > 1, allSharingClassIds, roadmapId);
  const endUserIds = endScope === 'all' ? [...allClassMembers.memberIds] : [...thisClassMembers.memberIds];
  const endClassIds = endScope === 'all' ? allSharingClassIds : classId ? [classId] : [];
  const endsWholeTarget = endScope === 'all' || sharedCount <= 1;

  const handleConfirm = async () => {
    setSubmitting(true);
    setConfirmError(false);
    try {
      await moveFocus({
        fromTargetId,
        toTargetId,
        userIds: elig.included.map(s => s.id),
        classIds: classIdsWithFocusRecipients(
          selectedClassIds,
          membership.memberIdsByClass,
          elig.included.map(s => s.id),
        ),
        focusMode: restrict ? 'restrict' : 'recommend',
      });
      const { summary, detail } = buildFocusMovedMessage(elig.unitName, reached);
      focusToast.current?.show(summary, detail);
      onMoved?.(toTargetId);
      op.current?.hide();
    } catch {
      setConfirmError(true);
    } finally {
      setSubmitting(false);
    }
    captureUnitFocusEvent(
      'unit_focus_moved',
      { classIds: selectedClassIds, targetId: toTargetId, targetGroupId: roadmapId, blockingEnabled: restrict },
      // The popover pre-selects the next unit the class hasn't completed yet, so this tells
      // apart teachers who take that suggestion from those who override it via the Edit step.
      { to_unit_is_default: toTargetId === defaultToTargetId },
    );
  };

  const handleEnd = async () => {
    setEnding(true);
    setEndError(false);

    captureUnitFocusEvent(
      'unit_focus_ended',
      { classIds: endClassIds, targetId: fromTargetId, targetGroupId: roadmapId, blockingEnabled: restrict },
      { scope_selected: endScope === 'all' ? 'all_classes' : 'this_class_only' },
    );
    try {
      await endFocus({ targetId: fromTargetId, userIds: endUserIds, classIds: endClassIds, endsWholeTarget });
      const scopeLabel = endScope === 'all' ? `all ${sharedCount} classes` : `${className} · ${roadmapName}`;
      const { summary, detail } = buildFocusEndedMessage(scopeLabel);
      focusToast.current?.show(summary, detail);
      op.current?.hide();
      onEnded?.();
    } catch {
      setEndError(true);
    } finally {
      setEnding(false);
    }
  };

  const confirmDisabled =
    !toTargetId || toTargetId === fromTargetId || loadingReach || reached === 0 || submitting || membership.error;

  return (
    <>
      <OverlayPanel
        ref={op}
        onShow={() => {
          setOpen(true);
          const next = defaultTargetId();
          setToTargetId(next);
          setDefaultToTargetId(next);
          setStep(next === '' ? 'select' : 'confirm');
          setApplyToAllClasses(true);
          setRestrict(resolveFocusRestrictDefault(focuses, fromTargetId));
          setConfirmError(false);
          setEndScope('this');
          setEnding(false);
          setEndError(false);
        }}
        onHide={() => {
          setOpen(false);
          setStep('confirm');
          setConfirmError(false);
          setEndError(false);
        }}
        className="!tw-w-96 !tw-rounded-2xl tw-border-solid !tw-border-2 !tw-border-neutral-200 !tw-shadow-[0px_0px_15px_rgba(132,123,111,0.2)]"
        pt={{ content: { className: 'p-0' } }}
      >
        {step === 'end' ? (
          <EndFocusStep
            className={className}
            roadmapName={roadmapName}
            thisClassCount={thisClassMembers.memberIds.size}
            allClassesCount={allClassMembers.memberIds.size}
            classCount={sharedCount}
            scope={endScope}
            onScopeChange={setEndScope}
            onBack={() => setStep('confirm')}
            onKeepFocus={() => op.current?.hide()}
            onEnd={handleEnd}
            ending={ending}
            errorText={endError ? 'Could not end focus. Please try again.' : undefined}
          />
        ) : step === 'select' ? (
          <UnitSelectStep
            roadmapName={roadmapName}
            units={units}
            currentUnitId={fromTargetId}
            selectedUnitId={toTargetId}
            onSelect={setToTargetId}
            onBack={() => setStep('confirm')}
            onDone={() => setStep('confirm')}
          />
        ) : (
          <div className="tw-flex tw-flex-col">
            <div className="tw-flex tw-flex-col tw-gap-3 tw-border-b-2 tw-border-neutral-100 tw-p-4">
              <span className={`${EYEBROW} tw-text-neutral-600`}>Move focus</span>

              <div className="tw-flex tw-items-stretch tw-gap-3">
                <div className={`${UNIT_CARD} tw-border-neutral-200 tw-bg-neutral-50`}>
                  <span className={`${EYEBROW} tw-text-neutral-600`}>Leaving</span>
                  <span className="tw-font-atkinson tw-text-xl tw-font-bold tw-leading-6 tw-text-neutral-950">
                    {fromUnit?.unitName ?? '—'}
                  </span>
                  <span className="tw-font-atkinson tw-text-sm tw-leading-4 tw-text-neutral-700">
                    {fromUnit?.summary}
                  </span>
                </div>

                <MoveRight size={16} className="tw-shrink-0 tw-self-center tw-text-neutral-700" aria-hidden />

                <div className={`${UNIT_CARD} tw-border-[#c2c9fe] tw-bg-indigo-50`}>
                  <span className={`${EYEBROW} tw-text-indigo-600`}>Focusing next</span>
                  <span className="tw-font-atkinson tw-text-xl tw-font-bold tw-leading-6 tw-text-indigo-600">
                    {toUnit?.unitName ?? 'Select a unit'}
                  </span>
                  <span className="tw-font-atkinson tw-text-sm tw-leading-4 tw-text-neutral-700">
                    {toUnit?.summary}
                  </span>
                  <button
                    type="button"
                    onClick={() => setStep('select')}
                    aria-label="Change unit"
                    className="tw-mt-1 tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-self-end tw-rounded-full tw-border-solid tw-border-2 tw-border-indigo-600 tw-bg-neutral-0 tw-px-3 tw-py-1 tw-font-atkinson tw-text-xs tw-font-bold tw-text-indigo-600"
                  >
                    Edit
                    <Pencil size={14} aria-hidden />
                  </button>
                </div>
              </div>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
              {sharedCount > 1 && (
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={applyToAllClasses}
                  aria-label={`Apply change to my other ${sharedCount - 1} classes sharing this focus`}
                  onClick={() => setApplyToAllClasses(v => !v)}
                  className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-xl tw-border-solid tw-border-2 tw-border-b-4 tw-border-neutral-200 tw-bg-white tw-p-3 tw-text-left"
                >
                  <span
                    aria-hidden
                    className={`tw-inline-flex tw-size-[22px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md ${
                      applyToAllClasses
                        ? 'tw-bg-indigo-600'
                        : 'tw-border-solid tw-border-2 tw-border-neutral-950 tw-bg-neutral-0'
                    }`}
                  >
                    {applyToAllClasses && <Check size={14} strokeWidth={3} className="tw-text-neutral-0" aria-hidden />}
                  </span>
                  <span className="tw-font-atkinson tw-text-sm tw-text-neutral-950">
                    Apply change to my <strong className="!tw-font-bold">other {sharedCount - 1} classes</strong>{' '}
                    sharing this focus
                  </span>
                </button>
              )}

              <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border-solid tw-border-2 tw-border-neutral-100 tw-bg-neutral-50 tw-px-4 tw-py-3">
                {membership.error ? (
                  <span role="alert" className="tw-font-atkinson tw-text-sm tw-text-[#b82828]">
                    Couldn&rsquo;t load class rosters, so we can&rsquo;t tell who gets this focus. Please try again.
                  </span>
                ) : !toTargetId ? (
                  <span className="tw-font-atkinson tw-text-sm tw-text-neutral-700">
                    Choose a unit to see who gets this focus.
                  </span>
                ) : (
                  <>
                    <CircleCheck size={18} className="tw-shrink-0 tw-text-[#007a54]" aria-hidden />
                    <span className="tw-font-atkinson tw-text-sm tw-text-neutral-950">
                      <FocusReachSummary reached={reached} total={total} loading={loadingReach} />
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="tw-px-4 tw-pb-4">
              <Button
                label="Confirm change"
                icon="pi pi-check"
                iconPos="right"
                onClick={handleConfirm}
                loading={submitting}
                disabled={confirmDisabled}
                className={CONFIRM_BTN}
              />
              {confirmError && (
                <p role="alert" className="tw-mx-0 tw-mb-0 tw-mt-2 tw-font-atkinson tw-text-sm tw-text-[#b82828]">
                  Could not move focus. Please try again.
                </p>
              )}
            </div>

            {/* Footer: divider sits below the button; the End-focus link is justified opposite the prompt. */}
            <div className="tw-flex tw-items-center tw-justify-between tw-border-t-2 tw-border-neutral-100 tw-px-4 tw-py-3">
              <span className="tw-font-atkinson tw-text-sm tw-text-neutral-700">Not moving forward?</span>
              <button
                type="button"
                onClick={() => {
                  setStep('end');
                  onEndFocusFlow?.();
                  // No scope is chosen yet, so report every class that shares the focus.
                  captureUnitFocusEvent('unit_focus_end_initiated', {
                    classIds: allSharingClassIds,
                    targetId: fromTargetId,
                    targetGroupId: roadmapId,
                    blockingEnabled: restrict,
                  });
                }}
                className="tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-p-0 tw-font-atkinson tw-text-sm tw-font-bold tw-text-indigo-600"
              >
                <CircleOff size={16} aria-hidden />
                End this focus
              </button>
            </div>
          </div>
        )}
      </OverlayPanel>

      <FocusSuccessToast ref={focusToast} />
    </>
  );
});
